import * as storage from './storage.js';

interface StartupRecoveryOptions {
  reloadApp?: () => void;
}

export function installGlobalStartupRecovery(options: StartupRecoveryOptions = {}): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const onError = (event: ErrorEvent) => {
    const message = runtimeErrorMessageFromEvent(event);
    if (!message || isIgnoredRuntimeError(message)) return;
    showStartupRecovery(options);
  };
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const message = runtimeErrorMessageFromRejection(event);
    if (!message || isIgnoredRuntimeError(message)) return;
    showStartupRecovery(options);
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
}

export function showStartupRecovery(options: StartupRecoveryOptions = {}): void {
  if (typeof document === 'undefined') return;

  const reloadApp = options.reloadApp ?? reloadPage;
  const shell = getOrCreateRecoveryShell();
  shell.innerHTML = '';
  shell.appendChild(createRecoveryPanel(reloadApp));
}

function getOrCreateRecoveryShell(): HTMLElement {
  const existing =
    document.getElementById('startup-recovery') ?? document.getElementById('startup-fallback');

  if (existing) {
    existing.id = 'startup-recovery';
    existing.setAttribute('role', 'alert');
    applyRecoveryShellStyle(existing);
    return existing;
  }

  const shell = document.createElement('div');
  shell.id = 'startup-recovery';
  shell.className = 'startup-fallback startup-recovery';
  shell.setAttribute('role', 'alert');
  applyRecoveryShellStyle(shell);
  document.body.prepend(shell);
  return shell;
}

function createRecoveryPanel(reloadApp: () => void): HTMLElement {
  const panel = document.createElement('div');
  Object.assign(panel.style, {
    width: 'min(100%, 430px)',
    display: 'grid',
    gap: '12px',
    padding: '24px',
    boxSizing: 'border-box',
    background: '#101b2c',
    border: '1px solid rgba(148, 163, 184, 0.28)',
    borderRadius: '16px',
    boxShadow: '0 24px 70px rgba(0, 0, 0, 0.35)',
  });

  const eyebrow = document.createElement('div');
  eyebrow.textContent = 'Truebend';
  Object.assign(eyebrow.style, {
    color: '#ff6a1a',
    fontSize: '0.75rem',
    fontWeight: '900',
    textTransform: 'uppercase',
  });

  const title = document.createElement('h1');
  title.textContent = 'Truebend needs a restart';
  Object.assign(title.style, {
    margin: '0',
    color: '#f8fafc',
    fontSize: '1.55rem',
    lineHeight: '1.08',
  });

  const copy = document.createElement('p');
  copy.textContent =
    'Close and reopen the app to reload the calculators. If it keeps happening, reset saved app data and start fresh.';
  Object.assign(copy.style, {
    margin: '0',
    color: '#a8b3c7',
    fontSize: '1rem',
    lineHeight: '1.45',
  });

  const buttonGroup = document.createElement('div');
  Object.assign(buttonGroup.style, {
    display: 'grid',
    gap: '8px',
  });

  const reloadButton = createRecoveryButton('Reload app', reloadApp, true);
  const resetButton = createRecoveryButton('Reset saved app data', () => {
    resetSavedAppData();
    reloadApp();
  });

  buttonGroup.append(reloadButton, resetButton);
  panel.append(eyebrow, title, copy, buttonGroup);
  return panel;
}

function createRecoveryButton(
  label: string,
  onClick: () => void,
  primary = false,
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.addEventListener('click', onClick);
  Object.assign(button.style, {
    minHeight: '48px',
    borderRadius: '12px',
    color: primary ? '#111827' : '#f8fafc',
    background: primary ? '#ff6a1a' : '#080f1a',
    border: primary ? '1px solid #ff6a1a' : '1px solid rgba(148, 163, 184, 0.28)',
    fontWeight: '900',
  });
  return button;
}

function applyRecoveryShellStyle(shell: HTMLElement): void {
  Object.assign(shell.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483647',
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: '24px',
    boxSizing: 'border-box',
    background: '#080f1a',
    color: '#f8fafc',
    fontFamily: 'Arial, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    textAlign: 'left',
  });
}

function resetSavedAppData(): void {
  try {
    storage.clearAll();
  } catch (error) {
    console.error('Truebend saved data reset failed', error);
  }
}

function reloadPage(): void {
  window.location.reload();
}

function runtimeErrorMessageFromEvent(event: ErrorEvent): string | undefined {
  if (typeof event.message === 'string' && event.message.length > 0) return event.message;
  if (event.error instanceof Error && event.error.message.length > 0) return event.error.message;
  return undefined;
}

function runtimeErrorMessageFromRejection(event: PromiseRejectionEvent): string | undefined {
  const reason = event.reason;
  if (reason instanceof Error && reason.message.length > 0) return reason.message;
  if (typeof reason === 'string' && reason.length > 0) return reason;
  return undefined;
}

function isIgnoredRuntimeError(message: string): boolean {
  return message.includes('ResizeObserver loop completed');
}
